
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, Bell, Shield } from "lucide-react";

export function PreferenciasForm() {
  const { toast } = useToast();
  const [preferences, setPreferences] = useState({
    newsletterSubscribed: true,
    emailNotifications: true,
    appointmentReminders: true,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleUnsubscribeNewsletter = async () => {
    setIsLoading(true);

    try {
      // Simular unsubscribe da newsletter
      setPreferences(prev => ({
        ...prev,
        newsletterSubscribed: false
      }));

      toast({
        title: "Newsletter cancelada",
        description: "Você foi removido da nossa lista de newsletter com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível cancelar a newsletter. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreferenceChange = (preference: string, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [preference]: value
    }));

    toast({
      title: "Preferência atualizada",
      description: "Sua preferência foi salva com sucesso.",
    });
  };

  return (
    <div className="space-y-6">
      {/* Newsletter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Newsletter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Receber Newsletter</Label>
              <p className="text-sm text-gray-600">
                Receba dicas de nutrição e novidades em seu email
              </p>
            </div>
            <Switch
              checked={preferences.newsletterSubscribed}
              onCheckedChange={(checked) => 
                handlePreferenceChange("newsletterSubscribed", checked)
              }
            />
          </div>
          
          {preferences.newsletterSubscribed && (
            <div className="pt-4 border-t">
              <Button
                onClick={handleUnsubscribeNewsletter}
                disabled={isLoading}
                variant="outline"
                className="w-full"
              >
                {isLoading ? "Cancelando..." : "Cancelar Newsletter"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notificações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notificações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Notificações por Email</Label>
              <p className="text-sm text-gray-600">
                Receba notificações importantes por email
              </p>
            </div>
            <Switch
              checked={preferences.emailNotifications}
              onCheckedChange={(checked) => 
                handlePreferenceChange("emailNotifications", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Lembretes de Consulta</Label>
              <p className="text-sm text-gray-600">
                Receba lembretes antes das suas consultas
              </p>
            </div>
            <Switch
              checked={preferences.appointmentReminders}
              onCheckedChange={(checked) => 
                handlePreferenceChange("appointmentReminders", checked)
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Privacidade */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Privacidade
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Seus dados estão protegidos de acordo com nossa política de privacidade. 
            Nunca compartilhamos suas informações pessoais com terceiros.
          </p>
          <Button variant="outline" size="sm">
            Ver Política de Privacidade
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
